import React, { useEffect, useState, useRef } from 'react'
import { Platform } from 'react-native'
import { ArrowLeft, Printer, Check, Filter } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Spinner, Text, XStack, YStack, ScrollView,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketPrintTags'>
  route: RouteProp<RootStackParamList, 'MarketPrintTags'>
}

interface Product {
  id: string; title: string; title_en: string | null; title_fi: string | null
  price: number; status: string; barcode: string | null; seller_id: string
  seller?: { display_name: string | null; first_name: string | null; last_name: string | null }
  space?: { name: string }
}

interface Market { id: string; name: string }

type TagSize = 'small' | 'medium' | 'large'
type StatusFilter = 'all' | 'for_sale' | 'reserved'

const TAG_SIZES = {
  small: { width: 50, height: 30, fontSize: 8, priceSize: 12 },
  medium: { width: 70, height: 40, fontSize: 10, priceSize: 16 },
  large: { width: 90, height: 50, fontSize: 12, priceSize: 20 },
}

const merchantPrimary = '#C07A5F'

export default function MarketPrintTagsScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t, language } = useLanguage()
  const [products, setProducts] = useState<Product[]>([])
  const [market, setMarket] = useState<Market | null>(null)
  const [loading, setLoading] = useState(true)
  const [tagSize, setTagSize] = useState<TagSize>('medium')
  const [showQR, setShowQR] = useState(true)
  const [showSeller, setShowSeller] = useState(true)
  const [showBarcode, setShowBarcode] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('for_sale')
  const printRef = useRef<any>(null)

  useEffect(() => { fetchData() }, [marketId, statusFilter])

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data: marketData, error: marketError } = await supabase
        .from('flea_markets').select('id, name').eq('id', marketId).single()
      if (marketError) throw marketError
      setMarket(marketData)

      let query = supabase
        .from('products')
        .select(`id, title, title_en, title_fi, price, status, barcode, seller_id, seller:users (display_name, first_name, last_name), reservation:space_reservations (space:market_spaces (name))`)
        .eq('flea_market_id', marketId)
        .order('title')

      if (statusFilter === 'for_sale') query = query.eq('status', 'for_sale')
      else if (statusFilter === 'reserved') query = query.eq('status', 'reserved')
      else query = query.in('status', ['for_sale', 'reserved'])

      const { data: prodData, error: prodError } = await query
      if (prodError) throw prodError

      setProducts((prodData || []).map((p: any) => ({
        ...p,
        seller: Array.isArray(p.seller) ? p.seller[0] : p.seller,
        space: p.reservation?.[0]?.space ? (Array.isArray(p.reservation[0].space) ? p.reservation[0].space[0] : p.reservation[0].space) : null,
      })))
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const getSellerName = (product: Product) => {
    if (!product.seller) return ''
    const { display_name, first_name, last_name } = product.seller
    if (display_name) return display_name
    if (first_name || last_name) return `${first_name || ''} ${last_name || ''}`.trim()
    return ''
  }

  const handlePrint = () => { if (Platform.OS === 'web') window.print() }

  const generateQRCodeUrl = (productId: string) => {
    const productUrl = `https://kirpputori.dev.sakualy.ai/product/${productId}`
    return `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(productUrl)}`
  }

  const generateBarcodeUrl = (barcode: string, width = 150, height = 40) =>
    `https://barcodeapi.org/api/128/${encodeURIComponent(barcode)}?width=${width}&height=${height}`

  const sizeConfig = TAG_SIZES[tagSize]

  const PillButton = ({ active, label, onPress }: { active: boolean; label: string; onPress: () => void }) => (
    <YStack
      px={12} py={6} br="$sm" cursor="pointer"
      bg={active ? merchantPrimary : '$backgroundSecondary'}
      bw={1} bbc={active ? merchantPrimary : '$borderColor'}
      onPress={onPress} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick"
    >
      <Text fontSize={12} fontWeight="500" color={active ? '#fff' : '$colorSecondary'} textTransform="capitalize">
        {label}
      </Text>
    </YStack>
  )

  const ToggleButton = ({ active, label, onPress }: { active: boolean; label: string; onPress: () => void }) => (
    <YStack
      px={12} py={6} br="$sm" cursor="pointer"
      bg={active ? `${merchantPrimary}20` : '$backgroundSecondary'}
      bw={1} bbc={active ? merchantPrimary : '$borderColor'}
      onPress={onPress} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick"
    >
      <XStack ai="center" gap="$xs">
        <Text fontSize={12} fontWeight="500" color={active ? merchantPrimary : '$colorSecondary'}>{label}</Text>
        {active && <Check size={14} color={merchantPrimary} />}
      </XStack>
    </YStack>
  )

  const HeaderBar = () => (
    <XStack jc="space-between" ai="center" px="$md" pt="$md" pb="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor" testID="no-print">
      <YStack py={8} pr="$md" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer">
        <XStack ai="center" gap="$xs">
          <ArrowLeft size={18} color={merchantPrimary} />
          <Text fontSize={16} color={merchantPrimary} fontWeight="500">{t('back') || 'Back'}</Text>
        </XStack>
      </YStack>
      <Text fontSize={18} fontWeight="bold" color="$color">{t('print_tags') || 'Print Tags'}</Text>
      <YStack w={60} />
    </XStack>
  )

  if (loading) {
    return (
      <YStack f={1} bg="#f5f5f5">
        <HeaderBar />
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} bg="#f5f5f5">
      <HeaderBar />

      {/* Filter Bar */}
      <XStack ai="center" p="$sm" bg="$backgroundCard" bbw={1} bbc="$borderColor" testID="no-print" gap="$sm">
        <Filter size={16} color="$colorSecondary" />
        <Text fontSize={14} color="$colorSecondary" fontWeight="500">{t('status') || 'Status'}:</Text>
        <XStack gap="$xs">
          {(['all', 'for_sale', 'reserved'] as StatusFilter[]).map((status) => (
            <PillButton key={status} active={statusFilter === status} label={status === 'all' ? (t('all') || 'All') : (t(status) || status)} onPress={() => setStatusFilter(status)} />
          ))}
        </XStack>
      </XStack>

      {/* Settings Bar */}
      <XStack flexWrap="wrap" ai="center" jc="space-between" p="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor" gap="$md" testID="no-print">
        <XStack ai="center" gap="$sm">
          <Text fontSize={14} color="$colorSecondary" fontWeight="500">{t('tag_size') || 'Tag Size'}:</Text>
          <XStack gap="$xs">
            {(['small', 'medium', 'large'] as TagSize[]).map((size) => (
              <PillButton key={size} active={tagSize === size} label={t(size) || size} onPress={() => setTagSize(size)} />
            ))}
          </XStack>
        </XStack>
        <XStack ai="center" gap="$sm">
          <ToggleButton active={showQR} label="QR" onPress={() => setShowQR(!showQR)} />
          <ToggleButton active={showSeller} label={t('seller') || 'Seller'} onPress={() => setShowSeller(!showSeller)} />
          <ToggleButton active={showBarcode} label={t('barcode') || 'Barcode'} onPress={() => setShowBarcode(!showBarcode)} />
        </XStack>
        <YStack
          bg={merchantPrimary} px="$lg" py={10} br="$md" cursor="pointer"
          onPress={handlePrint} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick"
        >
          <XStack ai="center" gap="$sm">
            <Printer size={16} color="#fff" />
            <Text color="#fff" fontSize={14} fontWeight="600">{t('print') || 'Print'}</Text>
          </XStack>
        </YStack>
      </XStack>

      {/* Info Banner */}
      {market && (
        <XStack bg={`${merchantPrimary}10`} p="$md" jc="space-between" ai="center" testID="no-print">
          <Text fontSize={14} color="$color" fontWeight="500">{market.name}</Text>
          <Text fontSize={14} color="$colorSecondary">{products.length} {t('products') || 'products'}</Text>
        </XStack>
      )}

      {/* Tags */}
      <ScrollView f={1} contentContainerStyle={{ padding: 16 }}>
        {products.length === 0 ? (
          <YStack p={40} ai="center" testID="no-print">
            <Text fontSize={16} color="$colorSecondary" textAlign="center">{t('no_products_to_print') || 'No products to print'}</Text>
          </YStack>
        ) : (
          <XStack flexWrap="wrap" gap="$md" jc="center" ref={printRef}>
            {products.map((product) => (
              <YStack
                key={product.id}
                bg="#fff" br={4} bw={1} bbc="#ddd" borderStyle="dashed"
                overflow="hidden" position="relative"
                w={sizeConfig.width * 3.78} h={sizeConfig.height * 3.78}
              >
                <XStack f={1} p={8}>
                  {showQR && (
                    <YStack jc="center" ai="center" mr={8}>
                      <img src={generateQRCodeUrl(product.id)} alt="QR" style={{ width: sizeConfig.height * 2.5, height: sizeConfig.height * 2.5 }} />
                    </YStack>
                  )}
                  <YStack f={1} jc="center" {...(!showQR && { ai: 'center' })}>
                    <Text fontSize={sizeConfig.fontSize} fontWeight="600" color="#333" mb={4} numberOfLines={2}>{getTitle(product)}</Text>
                    <Text fontSize={sizeConfig.priceSize} fontWeight="bold" color="#000" mb={2}>€{product.price?.toFixed(2) || '0.00'}</Text>
                    {showSeller && getSellerName(product) ? <Text fontSize={sizeConfig.fontSize - 2} color="#666" mb={2}>{getSellerName(product)}</Text> : null}
                    {showBarcode && product.barcode && (
                      <YStack ai="center" mt={4} mb={4}>
                        <img src={generateBarcodeUrl(product.barcode, sizeConfig.width * 2, sizeConfig.height * 0.6)} alt={product.barcode} style={{ width: sizeConfig.width * 2, height: sizeConfig.height * 0.6, objectFit: 'contain' }} />
                        <Text fontSize={sizeConfig.fontSize - 2} color="#444" fontFamily={Platform.OS === 'web' ? 'monospace' : undefined} mt={2}>{product.barcode}</Text>
                      </YStack>
                    )}
                    <Text fontSize={sizeConfig.fontSize - 2} color="#999" fontFamily={Platform.OS === 'web' ? 'monospace' : undefined}>
                      #{product.id.slice(-6).toUpperCase()}
                    </Text>
                  </YStack>
                </XStack>
                {/* Cut corner */}
                <YStack position="absolute" top={0} right={0} w={0} h={0} style={{ borderStyle: 'solid', borderWidth: 8, borderColor: 'transparent', borderTopColor: '#ddd', borderRightColor: '#ddd' } as any} />
              </YStack>
            ))}
          </XStack>
        )}
      </ScrollView>

      {Platform.OS === 'web' && (
        <style dangerouslySetInnerHTML={{ __html: `@media print { [data-testid="no-print"] { display: none !important; } body { margin: 0; padding: 0; } @page { margin: 10mm; } }` }} />
      )}
    </YStack>
  )
}
